import { useEffect, useMemo, useState } from 'react';

import type { Reaction } from '@/src/features/posts/post-service';

import {
  queuedCreateCommentToComment,
  queuedCreatePostToPublicPost,
  subscribeToOutbox,
  type QueuedCreateCommentOperation,
  type QueuedCreatePostOperation,
  type QueuedOutboxOperation,
  type QueuedSetCommentReactionOperation,
  type QueuedSetFollowingOperation,
  type QueuedSetPostLikeOperation,
  type QueuedSetPostReactionOperation,
} from './outbox-service';

export function useOutboxOperations() {
  const [operations, setOperations] = useState<QueuedOutboxOperation[]>([]);

  useEffect(() => subscribeToOutbox(setOperations), []);

  return operations;
}

export function usePendingPosts(uid: string | undefined) {
  const operations = useOutboxOperations();

  return useMemo(() => {
    if (!uid) {
      return [];
    }

    return operations
      .filter(
        (operation): operation is QueuedCreatePostOperation =>
          operation.type === 'create_post' && operation.uid === uid,
      )
      .map(queuedCreatePostToPublicPost);
  }, [operations, uid]);
}

export function usePendingComments({
  postId,
  uid,
}: {
  postId: string | undefined;
  uid: string | undefined;
}) {
  const operations = useOutboxOperations();

  return useMemo(() => {
    if (!postId || !uid) {
      return [];
    }

    return operations
      .filter(
        (operation): operation is QueuedCreateCommentOperation =>
          operation.type === 'create_comment' &&
          operation.uid === uid &&
          operation.payload.postId === postId,
      )
      .map(queuedCreateCommentToComment);
  }, [operations, postId, uid]);
}

export function useQueuedPostLike({
  postId,
  uid,
}: {
  postId: string | undefined;
  uid: string | undefined;
}) {
  const operations = useOutboxOperations();

  return useMemo(() => {
    if (!postId || !uid) {
      return null;
    }

    const matchingOperations = operations.filter(
      (candidate): candidate is QueuedSetPostLikeOperation =>
        candidate.type === 'set_post_like' &&
        candidate.uid === uid &&
        candidate.payload.post.id === postId,
    );
    const operation = matchingOperations[matchingOperations.length - 1];

    return operation?.payload.liked ?? null;
  }, [operations, postId, uid]);
}

export function useQueuedPostReaction({
  postId,
  uid,
}: {
  postId: string | undefined;
  uid: string | undefined;
}): Reaction | null | undefined {
  const operations = useOutboxOperations();

  return useMemo(() => {
    if (!postId || !uid) {
      return undefined;
    }

    const matchingOperations = operations.filter(
      (
        candidate,
      ): candidate is QueuedSetPostLikeOperation | QueuedSetPostReactionOperation =>
        (candidate.type === 'set_post_like' ||
          candidate.type === 'set_post_reaction') &&
        candidate.uid === uid &&
        candidate.payload.post.id === postId,
    );
    const operation = matchingOperations[matchingOperations.length - 1];

    if (!operation) {
      return undefined;
    }

    return operation.type === 'set_post_like'
      ? operation.payload.liked
        ? 'like'
        : null
      : operation.payload.reaction;
  }, [operations, postId, uid]);
}

export function useQueuedCommentReaction({
  commentId,
  postId,
  uid,
}: {
  commentId: string | undefined;
  postId: string | undefined;
  uid: string | undefined;
}): Reaction | null | undefined {
  const operations = useOutboxOperations();

  return useMemo(() => {
    if (!commentId || !postId || !uid) {
      return undefined;
    }

    const matchingOperations = operations.filter(
      (candidate): candidate is QueuedSetCommentReactionOperation =>
        candidate.type === 'set_comment_reaction' &&
        candidate.uid === uid &&
        candidate.payload.postId === postId &&
        candidate.payload.commentId === commentId,
    );
    const operation = matchingOperations[matchingOperations.length - 1];

    return operation ? operation.payload.reaction : undefined;
  }, [commentId, operations, postId, uid]);
}

export function useQueuedFollowing({
  targetUid,
  uid,
}: {
  targetUid: string | undefined;
  uid: string | undefined;
}) {
  const operations = useOutboxOperations();

  return useMemo(() => {
    if (!targetUid || !uid) {
      return null;
    }

    const matchingOperations = operations.filter(
      (candidate): candidate is QueuedSetFollowingOperation =>
        candidate.type === 'set_following' &&
        candidate.uid === uid &&
        candidate.payload.targetUid === targetUid,
    );
    const operation = matchingOperations[matchingOperations.length - 1];

    return operation?.payload.following ?? null;
  }, [operations, targetUid, uid]);
}
